import React, { useContext, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { Auth, Hub } from "aws-amplify";
import PropTypes from "prop-types";
import Login from "../Login/Login";
import UserContext from "../../context/User.context";
import "@pds-react/grid/dist/grid.min.css";
import MainPageLayout from "../MainPageLayout/MainPageLayout";
import ProtectedRoutes from "../MainPageLayout/ProtectedRoutes";
import NotFoundError from "../ErrorPages/NotFoundError/NotFoundError";
import UnauthorizedError from "../ErrorPages/UnauthorizedError/UnauthorizedError";
import CustodyAccountDetailsEdit from "../Management/CustodyAccount/CustodyAccount/CustodyAccountDetailsEdit/CustodyAccountDetailsEdit";
import CustodyAccountManagement from "../Management/CustodyAccount/CustodyAccountManagement";
import DateFilesReceivedManagement from "../Management/DateFilesReceived/DateFilesReceivedManagement";
import ServerError from "../ErrorPages/ServerError/ServerError";

function AppRouter({ calledFromTest }) {
  const { setUser: setUserContext, accessToken } = useContext(UserContext);

  const setUser = (user) => {
    // Verify users cognito groups
    let cognitoGroups = user.signInUserSession.accessToken.payload["cognito:groups"] || [];
    let hasReadWrite = cognitoGroups.includes("pgi-cashsweep-read-write-users");

    // Set login error if no congito groups exist
    let loginError;
    if (!hasReadWrite) {
      loginError = "You do not have access to view this page, please contact your administrator.";
    }

    // Set scope of user based on cognito groups
    let scope = hasReadWrite ? "ReadWrite" : "NoAccess";

    // Update user context based on gathered login details
    setUserContext({
      loading: false,
      email: user.attributes.email,
      name: user.attributes.name,
      loggedIn: true,
      accessToken: user.signInUserSession.accessToken,
      loginError,
      scope,
    });
  };

  const handleAuth = ({ payload }) => {
    switch (payload.event) {
      case "signIn":
        return setUser(payload.data);
      case "signOut":
        return setUser({ loggedIn: false, loginError: null });
    }
  };

  useEffect(() => {
    if (!calledFromTest) {
      Auth.currentAuthenticatedUser()
        .then(setUser)
        .catch((error) => {
          console.error("Auth error:", error);
          setUserContext({ loading: false });
        });

      Hub.listen("auth", handleAuth);

      return () => Hub.remove("auth", handleAuth);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!accessToken?.payload?.exp) {
        return;
      }
      const expireTimeLeft = Math.floor(accessToken.payload.exp - new Date().getTime() / 1000);
      if (expireTimeLeft < 600) {
        alert("You're less than 10 minutes away from being logged out");
      } else if (expireTimeLeft < 1800) {
        alert("You're less than 30 minutes away from being logged out");
      }
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [accessToken]);

  return (
    <Routes>
      <Route element={<MainPageLayout />}>
        <Route element={<ProtectedRoutes />}>
          <Route index path={"/"} element={<CustodyAccountManagement />}></Route>
          <Route path="/custody-account-management" element={<CustodyAccountManagement />} />
          <Route path="/custody-account-details/edit/:contact_company_id" element={<CustodyAccountDetailsEdit />} />
          <Route path="/custody-account-details/new" element={<CustodyAccountDetailsEdit />} />
          <Route index path={"/"} element={<DateFilesReceivedManagement />}></Route>
          <Route path="/date-files-received-management" element={<DateFilesReceivedManagement />} />
        </Route>
        <Route path="/login" element={<Login />}></Route>
        <Route path="/unauthorized" element={<UnauthorizedError />}></Route>
        <Route path="/unauthorized" element={<NotFoundError />} />
        <Route path="/error" element={<ServerError />} />
        <Route path="*" element={<NotFoundError />} />
      </Route>
    </Routes>
  );
}

AppRouter.propTypes = {
  calledFromTest: PropTypes.bool,
  newHistory: PropTypes.object,
};

export default AppRouter;
